import { useState, useEffect, useRef, useMemo } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  where,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebase';
import { ParkingSpace, ParkingSpaceWithContract, ParkingSpaceFormData } from '../../types/parkingSpace';
import { ParkingContract } from '../../types/parkingContract';

export const useParkingSpaces = () => {
  const [allSpaces, setAllSpaces] = useState<ParkingSpaceWithContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'occupied' | 'preparing' | 'vacant' | 'contracting'>('all');
  const [expiryFilter, setExpiryFilter] = useState<'all' | 'expired' | 'expiring-soon' | 'expiring-month'>('all');
  const unsubscribeRef = useRef<Unsubscribe | null>(null);
  const contractsUnsubscribeRef = useRef<Unsubscribe | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 현재 주차면과 계약 데이터
  const [spacesData, setSpacesData] = useState<ParkingSpace[]>([]);
  const [contractsData, setContractsData] = useState<ParkingContract[]>([]);

  // 실시간 주차면 구독
  const subscribeToSpaces = () => {
    try {
      const q = query(collection(db, 'parking_spaces'), orderBy('spaceNumber'));
      
      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          console.log('주차면 데이터 업데이트:', snapshot.size, '개');
          
          const spaces = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              spaceNumber: data.spaceNumber,
              status: data.status,
              currentContractId: data.currentContractId || undefined,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date()
            } as ParkingSpace;
          });
          
          setSpacesData(spaces);
          setConnected(true);
          setError(null);
          
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        },
        (error) => {
          console.error('주차면 구독 오류:', error);
          setError('주차면 데이터 연결에 실패했습니다.');
          setConnected(false);
          
          // 재연결 시도
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('주차면 구독 재연결 시도...');
            subscribeToSpaces();
          }, 5000);
        }
      );
      
      unsubscribeRef.current = unsubscribe;
    } catch (error) {
      console.error('주차면 구독 설정 실패:', error);
      setError('주차면 데이터 구독 설정에 실패했습니다.');
    }
  };

  // 실시간 계약 구독
  const subscribeToContracts = () => {
    try {
      const q = query(
        collection(db, 'parking_contracts'),
        where('status', 'in', ['contracting', 'active']),
        orderBy('spaceNumber')
      );
      
      const unsubscribe = onSnapshot(q,
        (snapshot) => {
          console.log('계약 데이터 업데이트:', snapshot.size, '개');
          
          const contracts = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              spaceId: data.spaceId,
              spaceNumber: data.spaceNumber,
              userUid: data.userUid,
              userInfo: data.userInfo,
              status: data.status,
              usagePeriod: data.usagePeriod ? {
                startDate: data.usagePeriod.startDate?.toDate() || new Date(),
                endDate: data.usagePeriod.endDate?.toDate() || new Date()
              } : undefined,
              vehicleInfo: data.vehicleInfo,
              notes: data.notes,
              contractSignedAt: data.contractSignedAt?.toDate(),
              applicationId: data.applicationId,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date()
            } as ParkingContract;
          });
          
          setContractsData(contracts);
        },
        (error) => {
          console.error('계약 구독 오류:', error);
          setError('계약 데이터 연결에 실패했습니다.');
        }
      );
      
      contractsUnsubscribeRef.current = unsubscribe;
    } catch (error) {
      console.error('계약 구독 설정 실패:', error);
      setError('계약 데이터 구독 설정에 실패했습니다.');
    }
  };

  // 주차면과 계약 데이터 조인
  const spacesWithContracts = useMemo(() => {
    return spacesData.map(space => {
      const currentContract = contractsData.find(contract => 
        contract.spaceId === space.id && 
        (contract.status === 'contracting' || contract.status === 'active')
      );
      
      return {
        ...space,
        currentContract
      } as ParkingSpaceWithContract;
    });
  }, [spacesData, contractsData]);

  // 만료일 계산 헬퍼 함수
  const getDaysUntilExpiry = (endDate: Date) => {
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // 검색, 상태, 만료일 필터링된 주차면 목록
  const filteredSpaces = useMemo(() => {
    return spacesWithContracts.filter(space => {
      // 검색어 필터링
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        space.spaceNumber.toLowerCase().includes(searchLower) ||
        (space.currentContract?.userInfo?.name || '').toLowerCase().includes(searchLower) ||
        (space.currentContract?.userInfo?.phoneNumber || '').toLowerCase().includes(searchLower) ||
        (space.currentContract?.userInfo?.organization || '').toLowerCase().includes(searchLower) ||
        (space.currentContract?.vehicleInfo || '').toLowerCase().includes(searchLower) ||
        (space.currentContract?.notes || '').toLowerCase().includes(searchLower)
      );

      // 상태 필터링
      const matchesStatus = statusFilter === 'all' || space.status === statusFilter;

      // 만료일 필터링
      let matchesExpiry = true;
      if (expiryFilter !== 'all' && space.currentContract?.usagePeriod?.endDate) {
        const daysUntilExpiry = getDaysUntilExpiry(space.currentContract.usagePeriod.endDate);
        
        switch (expiryFilter) {
          case 'expired':
            matchesExpiry = daysUntilExpiry < 0;
            break;
          case 'expiring-soon':
            matchesExpiry = daysUntilExpiry >= 0 && daysUntilExpiry <= 7;
            break;
          case 'expiring-month':
            matchesExpiry = daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
            break;
        }
      } else if (expiryFilter !== 'all') {
        // 만료일이 없는 주차면은 만료일 필터에서 제외
        matchesExpiry = false;
      }

      return matchesSearch && matchesStatus && matchesExpiry;
    });
  }, [spacesWithContracts, searchTerm, statusFilter, expiryFilter]);

  // 주차면 추가
  const addSpace = async (formData: ParkingSpaceFormData) => {
    try {
      const now = Timestamp.now();
      
      // 1. 주차면 기본 정보 추가
      const newSpace = {
        spaceNumber: formData.spaceNumber,
        status: formData.status,
        createdAt: now,
        updatedAt: now
      };

      const spaceRef = await addDoc(collection(db, 'parking_spaces'), newSpace);

      // 2. 사용중이거나 계약진행인 경우 계약 정보 추가
      if ((formData.status === 'occupied' || formData.status === 'contracting') && formData.userName) {
        const newContract = {
          spaceId: spaceRef.id,
          spaceNumber: formData.spaceNumber,
          userUid: '', // 관리자가 직접 추가할 때는 빈 문자열
          userInfo: {
            name: formData.userName,
            phoneNumber: formData.userPhoneNumber || '',
            organization: formData.userOrganization || ''
          },
          status: formData.status === 'occupied' ? 'active' : 'contracting',
          usagePeriod: (formData.startDate && formData.endDate) ? {
            startDate: Timestamp.fromDate(new Date(formData.startDate)),
            endDate: Timestamp.fromDate(new Date(formData.endDate))
          } : undefined,
          vehicleInfo: formData.vehicleInfo || '',
          notes: formData.notes || '',
          createdAt: now,
          updatedAt: now
        };

        const contractRef = await addDoc(collection(db, 'parking_contracts'), newContract);

        // 3. 주차면에 현재 계약 ID 연결
        await updateDoc(spaceRef, {
          currentContractId: contractRef.id,
          updatedAt: now
        });
      }

      return true;
    } catch (error) {
      console.error('주차면 추가 실패:', error);
      return false;
    }
  };

  // 주차면 수정
  const updateSpace = async (spaceId: string, formData: ParkingSpaceFormData) => {
    try {
      const now = Timestamp.now();
      const spaceRef = doc(db, 'parking_spaces', spaceId);
      
      // 기존 주차면 정보 조회
      const spaceDoc = await getDoc(spaceRef);
      if (!spaceDoc.exists()) {
        throw new Error('주차면을 찾을 수 없습니다.');
      }
      
      const spaceData = spaceDoc.data();
      const oldContractId = spaceData.currentContractId;

      // 1. 주차면 기본 정보 업데이트
      const updateData: any = {
        spaceNumber: formData.spaceNumber,
        status: formData.status,
        updatedAt: now
      };

      // 2. 계약 정보 처리
      if ((formData.status === 'occupied' || formData.status === 'contracting') && formData.userName) {
        // 새로운 계약 생성 또는 기존 계약 업데이트
        const contractData = {
          spaceId: spaceId,
          spaceNumber: formData.spaceNumber,
          userUid: '', // 관리자가 직접 수정할 때는 빈 문자열
          userInfo: {
            name: formData.userName,
            phoneNumber: formData.userPhoneNumber || '',
            organization: formData.userOrganization || ''
          },
          status: formData.status === 'occupied' ? 'active' : 'contracting',
          usagePeriod: (formData.startDate && formData.endDate) ? {
            startDate: Timestamp.fromDate(new Date(formData.startDate)),
            endDate: Timestamp.fromDate(new Date(formData.endDate))
          } : undefined,
          vehicleInfo: formData.vehicleInfo || '',
          notes: formData.notes || '',
          updatedAt: now
        };

                 if (oldContractId) {
           // 기존 계약 업데이트
           await updateDoc(doc(db, 'parking_contracts', oldContractId), contractData);
           updateData.currentContractId = oldContractId;
         } else {
           // 새 계약 생성
           const newContractData = {
             ...contractData,
             createdAt: now
           };
           const contractRef = await addDoc(collection(db, 'parking_contracts'), newContractData);
           updateData.currentContractId = contractRef.id;
         }
      } else {
        // 사용중이나 계약진행이 아닌 경우 기존 계약 완료 처리
        if (oldContractId) {
          await updateDoc(doc(db, 'parking_contracts', oldContractId), {
            status: 'completed',
            updatedAt: now
          });
        }
        updateData.currentContractId = null;
      }

      await updateDoc(spaceRef, updateData);
      return true;
    } catch (error) {
      console.error('주차면 수정 실패:', error);
      return false;
    }
  };

  // 주차면 삭제
  const deleteSpace = async (spaceId: string) => {
    try {
      // 먼저 연관된 계약들을 완료 처리
      const contractsQuery = query(
        collection(db, 'parking_contracts'),
        where('spaceId', '==', spaceId),
        where('status', 'in', ['contracting', 'active'])
      );
      
      const contractsSnapshot = await getDocs(contractsQuery);
      const updatePromises = contractsSnapshot.docs.map(contractDoc =>
        updateDoc(contractDoc.ref, {
          status: 'terminated',
          updatedAt: Timestamp.now()
        })
      );
      
      await Promise.all(updatePromises);
      
      // 주차면 삭제
      await deleteDoc(doc(db, 'parking_spaces', spaceId));
      return true;
    } catch (error) {
      console.error('주차면 삭제 실패:', error);
      return false;
    }
  };

  // 정리 함수
  const cleanup = () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (contractsUnsubscribeRef.current) {
      contractsUnsubscribeRef.current();
      contractsUnsubscribeRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  // 초기 구독 설정
  useEffect(() => {
    subscribeToSpaces();
    subscribeToContracts();
    setLoading(false);

    return cleanup;
  }, []);

  return {
    spaces: filteredSpaces,
    allSpaces: spacesWithContracts, // 필터링 이전의 전체 주차면 목록
    loading,
    connected,
    error,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    expiryFilter,
    setExpiryFilter,
    addSpace,
    updateSpace,
    deleteSpace
  };
}; 